import type { VueCookies } from 'vue-cookies';

import { useCookies } from '@/composables/cookies';
import { useFischer } from '@/composables/fischerPlugin';
import { logoutFunction } from '@/composables/user';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import { postFormEncoded } from '@/lib/postFormEncoded';
import { getLayoutdataUrl } from '@/loadLayoutData';
import parseTokenData from '@/utils/parseTokenData';

// Client-only singletons, guarded by IS_SSR checks in schedule functions
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let refreshChannelListenerInitialized = false;

const refreshChannel = 'token-refresh';

type RefreshMessage =
  | ['refreshing']
  | [
      'refreshed',
      {
        accessToken: string;
        idToken: string;
        expiresIn: number;
        refreshToken?: string;
        // the tokens of an impersonating agent, already in the shared assisted service session
        agent?: boolean;
      },
    ];

const broadcastRefreshMessage = (message: RefreshMessage) => {
  try {
    const bc = new BroadcastChannel(refreshChannel);
    bc.postMessage(message);
    bc.close();
  } catch {
    // BroadcastChannel not supported
  }
};

const clearRefreshTimer = () => {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

const applyRefreshedTokens = (
  fischer: FischerPlugin,
  vueCookies: VueCookies,
  accessToken: string,
  idToken: string,
  expiresIn: number,
  newRefreshToken?: string,
) => {
  if (IS_SHOWROOM) {
    const { exp }: { exp: number } = parseTokenData(idToken);
    const ttl = exp - Date.now() / 1000;
    fischer.user.setIdToken(idToken, String(ttl));
    fischer.user.setAccessToken(accessToken, String(ttl));
    if (newRefreshToken) {
      // The refresh token cookie is showroom-only; in production it lives in the Sitecore session
      fischer.user.setRefreshToken(newRefreshToken);
    }
  } else {
    fischer.user.setAccessToken(accessToken, '');
    fischer.user.setIdToken(idToken);
  }

  scheduleRefreshFunction(fischer, vueCookies, expiresIn);
};

const doShowroomRefresh = async (
  fischer: FischerPlugin,
  vueCookies: VueCookies,
): Promise<boolean> => {
  const currentRefreshToken = fischer.user.refreshToken();
  if (!currentRefreshToken) {
    return false;
  }

  // Notify other tabs that this tab is handling the refresh
  broadcastRefreshMessage(['refreshing']);

  try {
    const response = await postFormEncoded(
      `${import.meta.env.VITE_API_URL}/authorizationserver/oauth/token`,
      {
        client_id: fischer.getClientId(),
        grant_type: 'refresh_token',
        refresh_token: currentRefreshToken,
      },
    );

    if (!response.ok) {
      console.error('Token refresh failed:', response.status);
      return false;
    }

    const json = await response.json();
    const accessToken = json.access_token;
    const idToken = json.id_token;
    const expiresIn = Number(json.expires_in);
    const newRefreshToken = json.refresh_token;

    if (!accessToken || !idToken || !expiresIn || Number.isNaN(expiresIn)) {
      console.error('Token refresh: missing or invalid tokens in response');
      return false;
    }

    applyRefreshedTokens(fischer, vueCookies, accessToken, idToken, expiresIn, newRefreshToken);

    // Broadcast new tokens so other tabs can apply them without making their own request
    broadcastRefreshMessage([
      'refreshed',
      { accessToken, idToken, expiresIn, refreshToken: newRefreshToken },
    ]);

    return true;
  } catch (error) {
    console.error('Token refresh error:', error);
    return false;
  }
};

/**
 * Production refresh: the Sitecore session owns the refresh token. Requesting the
 * layout service with `refresh=true` makes the server refresh the session user's
 * tokens when the id token is near expiry (no-op when nothing is stored) and return
 * the rotated tokens in the same response. A rejected refresh token (400/401) logs
 * the user out server-side — the client keeps its tokens and the next failing API
 * call triggers the regular logout path.
 */
const doSessionRefresh = async (
  fischer: FischerPlugin,
  vueCookies: VueCookies,
): Promise<boolean> => {
  // Notify other tabs that this tab is handling the refresh
  broadcastRefreshMessage(['refreshing']);

  try {
    const layoutResponse = await fetch(
      `${getLayoutdataUrl('', fischer.pageId(), fischer.siteLanguage())}&refresh=true`,
    );
    if (!layoutResponse.ok) {
      console.error('Session token refresh failed:', layoutResponse.status);
      return false;
    }

    const layoutData = await layoutResponse.json();
    // while an agent impersonates, the page user is the customer and the agent's tokens come
    // separately - they are the ones every assisted service call is made with
    const assistedService = fischer.user.getAssistedService();
    const agent = Boolean(assistedService?.user);
    const user = layoutData?.sitecore?.user;
    const tokens = agent ? user?.agentTokens : user;
    const accessToken = tokens?.accessToken;
    const idToken = tokens?.idToken;
    if (!accessToken || !idToken) {
      console.error('Session token refresh: no tokens in layout data');
      return false;
    }

    const exp = parseTokenData(idToken)?.exp;
    const expiresIn = exp ? exp - Date.now() / 1000 : 0;
    if (expiresIn <= 0) {
      console.error('Session token refresh: refreshed id token is expired or invalid');
      return false;
    }

    if (assistedService) {
      fischer.setAssistedServiceTokens({
        token: accessToken,
        idToken,
        expiresIn: String(Math.round(expiresIn)),
      });
    }
    if (agent) {
      scheduleRefreshFunction(fischer, vueCookies, expiresIn);
    } else {
      applyRefreshedTokens(fischer, vueCookies, accessToken, idToken, expiresIn);
    }

    // Broadcast new tokens so other tabs can apply them without making their own request
    broadcastRefreshMessage(['refreshed', { accessToken, idToken, expiresIn, agent }]);

    return true;
  } catch (error) {
    console.error('Session token refresh error:', error);
    return false;
  }
};

const doRefresh = (fischer: FischerPlugin, vueCookies: VueCookies): Promise<boolean> =>
  IS_SHOWROOM ? doShowroomRefresh(fischer, vueCookies) : doSessionRefresh(fischer, vueCookies);

export const refreshTokensFunction = async (
  fischer: FischerPlugin,
  vueCookies: VueCookies,
): Promise<boolean> => {
  if (refreshInFlight !== null) return refreshInFlight;
  refreshInFlight = doRefresh(fischer, vueCookies)
    .then(async (refreshed) => {
      // an agent session cannot outlive its token: every assisted service call would fail
      if (!refreshed && fischer.user.getAssistedService()) {
        await logoutFunction({ reason: 'InvalidToken' }, fischer, vueCookies);
      }
      return refreshed;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
};

/** The refresh currently running, so a failed call can wait for it instead of logging out. */
export const pendingTokenRefresh = (): Promise<boolean> | null => refreshInFlight;

export const scheduleRefreshFunction = (
  fischer: FischerPlugin,
  vueCookies: VueCookies,
  expiresIn: number,
) => {
  // Clear any prior timer before setting a new one
  clearRefreshTimer();

  if (IS_SSR) {
    return;
  }

  // Refresh at 80% of lifetime, but never later than 30s before expiry
  const refreshAfterMs = Math.min(expiresIn * 0.8 * 1000, (expiresIn - 30) * 1000);

  if (refreshAfterMs <= 0) {
    return;
  }

  if (IS_SHOWROOM) {
    // @ts-expect-error undeclared debug code
    window.refreshTestCall = () => refreshTokensFunction(fischer, vueCookies);
  }

  refreshTimer = setTimeout(() => {
    refreshTokensFunction(fischer, vueCookies);
  }, refreshAfterMs);
};

/**
 * Listen for refresh events from other tabs.
 * - 'refreshing': another tab is handling the refresh, cancel our timer to avoid duplicate requests
 * - 'refreshed': another tab completed refresh, apply the new tokens and reschedule
 */
export const initRefreshChannelListener = (fischer: FischerPlugin, vueCookies: VueCookies) => {
  if (IS_SSR || refreshChannelListenerInitialized) {
    return;
  }
  refreshChannelListenerInitialized = true;

  try {
    const bc = new BroadcastChannel(refreshChannel);
    bc.onmessage = (event) => {
      const [type, data] = event?.data || [];

      if (type === 'refreshing') {
        // Another tab is refreshing — cancel our timer to avoid a duplicate request
        clearRefreshTimer();
      } else if (type === 'refreshed' && data?.agent) {
        // the agent session is shared storage and already holds the tokens
        scheduleRefreshFunction(fischer, vueCookies, data.expiresIn);
      } else if (type === 'refreshed' && data) {
        // Another tab completed the refresh — apply tokens locally and reschedule
        applyRefreshedTokens(
          fischer,
          vueCookies,
          data.accessToken,
          data.idToken,
          data.expiresIn,
          data.refreshToken,
        );
      }
    };
  } catch {
    // BroadcastChannel not supported
  }
};

export const scheduleRefreshFromTokenFunction = (
  fischer: FischerPlugin,
  vueCookies: VueCookies,
) => {
  if (IS_SSR) {
    return;
  }

  const idTokenData = fischer.user.idTokenData();
  if (!idTokenData?.exp) {
    return;
  }

  // The refresh token cookie only exists in the showroom; in production the
  // Sitecore session holds it, so the id token expiry alone drives the timer.
  if (IS_SHOWROOM && !fischer.user.refreshToken()) {
    return;
  }

  const remainingSeconds = idTokenData.exp - Date.now() / 1000;
  // scheduleRefreshFunction gives up on a token this close to expiry. An agent coming back to an
  // idle tab would lose the session to the next call, so refresh it now instead.
  if (remainingSeconds <= 30 && fischer.user.getAssistedService()) {
    refreshTokensFunction(fischer, vueCookies);
  } else if (remainingSeconds > 0) {
    scheduleRefreshFunction(fischer, vueCookies, remainingSeconds);
  }
};

export const clearRefreshFunction = (fischer: FischerPlugin) => {
  clearRefreshTimer();
  fischer.user.clearRefreshToken();
};

export const useTokenRefresh = () => {
  const fischer = useFischer();
  const { cookies: vueCookies } = useCookies();

  const scheduleRefresh = (expiresIn: number) =>
    scheduleRefreshFunction(fischer, vueCookies, expiresIn);

  const clearRefresh = () => clearRefreshFunction(fischer);

  return {
    scheduleRefresh,
    clearRefresh,
  };
};
